/**
 * ColumnTextRow — `column.text` renderer. Paints typed prose in the
 * `column` slot and edits its parent dataSource's cells in place
 * (presentations are editors: a renderer both paints and edits its
 * source's cells).
 *
 * Hydrated as the first child of any dataSource whose shape resolves
 * to `column.text` via `resolveDefaultRendererId`. The source is
 * looked up through `this.parent` so the same class works whether
 * the child was freshly synthesized by the hydrator or parsed from a
 * serialized raw renderer row.
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import type { NaiveDate } from "@tento-chrono";

import type { Actor } from "../../tx/invalidation.ts";
import { RendererRow } from "./renderer-row.ts";
import { SourceCells } from "./source-cells.ts";
import { SourceRow } from "./source-row.ts";
import { sourceWriterOf } from "./source-row.ts";

/**
 * **The writers of a `column.text` renderer** — checked text write and clear
 * of its source row's cells (`ColumnTextRow.Writer`), through the source's
 * checked writer (`sourceWriterOf`), so a canonical (`:~`) row's write
 * rejection still applies. Not on the row: a
 * row a read hands out (`handle.model`, `row.variant`, `RowKind.of`) —
 * and the live class `liveRowOf` reaches — carries no writer. The package's
 * own ops write through it, and this repository's tests through
 * `@tento-lona/sheets/internal-testing`; an app and the SDK's own front-ends
 * (sheets-ui, the CLI) write the cell through the SDK row's checked verbs
 * (`row.writableSingle()?.write(CellTarget.date(day), value, actor)`).
 */
export function columnTextWriterOf(row: ColumnTextRow): ColumnTextRow.Writer {
  return Object.freeze({
    row,
    write: async (date: NaiveDate.Partial, text: string, actor: Actor) => {
      await sourceWriterOf(row.source).write<string>(date, text, null, actor);
    },
    clear: async (date: NaiveDate.Partial, actor: Actor) => {
      await sourceWriterOf(row.source).clear(date, null, actor);
    },
  });
}

/** The `column.text` renderer row: paints its source row's text cells. */
export class ColumnTextRow extends RendererRow {
  /** The dataSource this renderer paints + edits. */
  get source(): SourceRow {
    const p = this.parent;
    if (!(p instanceof SourceRow)) {
      throw new Error(
        `ColumnTextRow: parent of ${this.id.rawId} is not a SourceRow ` +
          `(got ${p?.constructor.name ?? "undefined"}). A column.* ` +
          `renderer must be a direct child of its dataSource.`,
      );
    }
    return p;
  }

  /**
   * False when the source rejects cell writes (formula rows,
   * provider-owned `metadata.readOnly` rows) — the UI renders the
   * cell read-only instead of letting a text write throw.
   */
  get editable(): boolean {
    // `editable` is read from the cell-render onData path, which can fire on a
    // renderer that was transiently detached from its source (its row left the
    // tree during an in-place structural mutation while a stale cell-handle
    // subscription still fires). A detached renderer reports non-editable
    // instead of letting `source` throw — the zombie update no-ops; the freshly
    // re-parented renderer paints normally. Edit paths still use strict `source`.
    const p = this.parent;
    return p instanceof SourceRow ? p.cellsWritable : false;
  }

  /** The loaded text for `date` (a synchronous cache read); `null` if unset. */
  peekText(date: NaiveDate.Partial): Option<string> {
    const cell = SourceCells.dates(this.source.peekCells(date.toRange()))[0];
    return cell == null ? null : (cell.value as Option<string>);
  }



  /**
   * Optional placeholder prompt displayed in empty cells for the
   * current column (consumed by `text-plugin`). Stored on the
   * source's attribute bag.
   */
  get prompt(): Option<string> {
    return Json.string(this.source.attributes(), "prompt");
  }
}

/** Types that go with `ColumnTextRow`: `Writer`. */
export namespace ColumnTextRow {
  /** A `column.text` renderer's writers, reached through `columnTextWriterOf(row)` only. */
  export interface Writer {
    /** The row these writers write. */
    readonly row: ColumnTextRow;
    /** Checked text write for `date`; `actor` tags the originator for self-echo attribution. */
    write(date: NaiveDate.Partial, text: string, actor: Actor): Promise<void>;
    /** Checked clear: an emptied editor deletes the cell instead of storing an empty string. */
    clear(date: NaiveDate.Partial, actor: Actor): Promise<void>;
  }
}
